/**
 * StripePayments.jsx – Phase 1a: Zahlungsliste (Read-Only)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Filter, RefreshCw, XCircle, AlertTriangle, CheckCircle2, Clock, ChevronDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_OPTIONS = [
  { value: '', label: 'Alle Zahlungen' },
  { value: 'succeeded', label: 'Erfolgreich' },
  { value: 'pending', label: 'Ausstehend' },
  { value: 'failed', label: 'Fehlgeschlagen' },
];

function PayStatusIcon({ status }) {
  if (status === 'succeeded') return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-red-500" />;
  return <Clock className="h-4 w-4 text-amber-500" />;
}

function PayStatusBadge({ status, statusDe }) {
  const c = {
    succeeded: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  }[status] || 'bg-gray-100 text-gray-600';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c}`}>{statusDe || status}</span>;
}

export default function StripePayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  const load = async (after = null, st = status) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: 30 });
      if (st) params.set('status', st);
      if (after) params.set('starting_after', after);
      const res = await axios.get(`${API}/api/superadmin/stripe-cc/payments?${params}`, { headers: authHeader() });
      if (after) {
        setPayments(prev => [...prev, ...(res.data.payments || [])]);
      } else {
        setPayments(res.data.payments || []);
      }
      setHasMore(res.data.has_more || false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fehler beim Laden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(null, status); }, [status]); // eslint-disable-line

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="pl-8 pr-8 h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none appearance-none"
            data-testid="stripe-payments-filter"
          >
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        </div>
        <Button variant="outline" size="sm" onClick={() => load(null, status)} disabled={loading} className="gap-1.5">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </Button>
        <span className="text-xs text-muted-foreground">{payments.length} Zahlungen geladen</span>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 rounded-xl text-sm text-red-700">
          <XCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Liste */}
      {loading && payments.length === 0 ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="space-y-2">
            {payments.map(p => (
              <div key={p.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4"
                data-testid={`payment-row-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <PayStatusIcon status={p.status} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold">{p.customer_name}</p>
                        <PayStatusBadge status={p.status} statusDe={p.status_de} />
                        {p.refunded && (
                          <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">Erstattet</span>
                        )}
                        {p.is_disputed && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">Streit</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-muted-foreground">{p.payment_method}</span>
                        <span className="text-xs text-muted-foreground">{p.date}</span>
                        {p.description && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{p.description}</span>}
                      </div>
                      {p.failure_reason && (
                        <div className="flex items-center gap-1.5 mt-1.5 p-2 bg-red-50 dark:bg-red-900/10 rounded-lg">
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                          <span className="text-xs text-red-600 dark:text-red-400">{p.failure_reason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold">{p.amount_display}</p>
                    <p className="text-xs text-muted-foreground">{p.currency}</p>
                    {p.amount_refunded_display && (
                      <p className="text-xs text-purple-600">–{p.amount_refunded_display}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {payments.length === 0 && !loading && (
              <p className="text-center py-12 text-sm text-muted-foreground">Keine Zahlungen gefunden</p>
            )}
          </div>

          {hasMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => {
                const lastId = payments[payments.length - 1]?.id;
                if (lastId) load(lastId);
              }} disabled={loading} className="gap-1.5">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Mehr laden
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
